import { matchSorter } from 'match-sorter';
import type { AppCommand, CurriculumManifest, Problem, SearchDocument } from '@cpu/domain';

export const conceptAliases: Record<string, string[]> = {
  'fenwick tree': ['bit', 'binary indexed tree'],
  'longest increasing subsequence': ['lis'],
  'lowest common ancestor': ['lca'],
  'disjoint set union': ['dsu', 'union find', 'union-find'],
  'strongly connected components': ['scc'],
  'sliding window': ['two pointers', 'window', 'contiguous range'],
  'prefix sum': ['range sum', 'cumulative sum'],
  'convex hull trick': ['cht'],
  'heavy light decomposition': ['hld'],
  'dynamic programming': ['dp']
};

export function createSearchDocuments(manifest: CurriculumManifest, problems: Problem[]): SearchDocument[] {
  const moduleDocuments: SearchDocument[] = manifest.modules.map((module) => ({
    id: module.id,
    type: 'module',
    title: module.shortTitle,
    subtitle: `${module.id} · ${module.category}`,
    keywords: [module.id, module.wave, ...(conceptAliases[module.shortTitle.toLowerCase()] ?? [])],
    href: `/atlas/${module.slug}`,
    preview: module.description
  }));
  const problemDocuments: SearchDocument[] = problems.map((problem) => ({
    id: problem.id,
    type: 'problem',
    title: problem.name,
    subtitle: `${problem.moduleId} · ${problem.difficulty}`,
    keywords: [problem.atomId, problem.technique, problem.sourceLabel],
    href: `/practice/${problem.id}`,
    preview: problem.technique
  }));
  return [...moduleDocuments, ...problemDocuments];
}

export function searchDocuments(documents: SearchDocument[], query: string, limit = 18): SearchDocument[] {
  const normalized = query.trim();
  if (!normalized) return documents.slice(0, limit);
  return matchSorter(documents, normalized, {
    keys: ['title', 'subtitle', 'keywords', 'preview'],
    threshold: matchSorter.rankings.CONTAINS
  }).slice(0, limit);
}

export function searchCommands(commands: AppCommand[], query: string): AppCommand[] {
  if (!query.trim()) return commands;
  return matchSorter(commands, query, { keys: ['title', 'subtitle', 'keywords'] });
}
